import { createClient } from '@supabase/supabase-js';
import { viewAsProId } from './viewAs';
import { isCell } from './cell/config';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if ((!SUPABASE_URL || !SUPABASE_KEY) && !isCell()) {
  // Loud failure so a missing .env in dev doesn't silently break login.
  // In prod (Cloudflare Pages), these are baked into the build at deploy
  // time via the project's env-var settings.
  // eslint-disable-next-line no-console
  console.error(
    '[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY ' +
    '— copy .env.example to .env.local and fill them in.'
  );
}

/**
 * Singleton Supabase client. RLS policies on every table do the
 * authorization; the publishable key is safe to ship in the browser.
 *
 * After magic-link login, the SDK persists the session in localStorage
 * automatically and refreshes the access token in the background. Any
 * supabase.from(…) / supabase.functions.invoke(…) call thereafter
 * sends the user's JWT, so RLS knows who's asking.
 */
function makeClient() {
  return createClient(SUPABASE_URL ?? '', SUPABASE_KEY ?? '', {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // captures the magic-link `?code=…` redirect
      flowType: 'pkce',
    },
  });
}

/**
 * ON THE CELL BUILD (move map M28) there is no Supabase: every screen action calls Tenean's API (lib/cell/api.ts).
 * A call that was missed lands here and THROWS, naming itself — it can never quietly reach Supabase. The parity walk
 * and cell-seams.test.ts catch it before it ships.
 */
class NotOnCellError extends Error {
  constructor(what: string) { super(`Supabase is not used on the Tenean cell build (${what}) — this call has no API endpoint yet.`); this.name = 'NotOnCellError'; }
}
function notOnCell(path: string): unknown {
  return new Proxy(function () { /* callable */ }, {
    get(_t, k) { if (k === 'then') return undefined; return notOnCell(`${path}.${String(k)}`); },
    apply() { throw new NotOnCellError(path); },
  });
}

export const supabase = (isCell() ? notOnCell('supabase') : makeClient()) as ReturnType<typeof makeClient>;

export const PROJECT_URL = SUPABASE_URL ?? '';

// ── VIEW-AS IS READ ONLY, ENFORCED AT THE CLIENT'S ONE DOOR (PRO-VIEW-1) ─────────────
// Every mutation the SDK can express — table writes, non-allowlisted rpcs, storage
// writes, function invokes — resolves to a refusal while view-as is active. This is the
// first fence; the second is structural: server-side, every pro-owned write keys on the
// caller's REAL identity, which has no pro row. The stub is an infinitely-chainable
// thenable so any builder chain (.update().eq()…) resolves to a normal {data,error}
// shape and surfaces through each screen's existing error handling.
// (The cell build has no Supabase client to wrap; lib/cell/api.ts carries the same fence.)
const RO_MSG = 'View-as is read only — nothing was changed.';
const READ_RPCS = new Set([
  'pro_hub_feature_enabled', 'pro_market_profile', 'pro_market_markets',
  'pro_market_feed', 'pro_ig_insights', 'pro_ig_posts', 'log_pro_view',
  // PRO-DRAW-1: the pro's drawing and who signed up — reads, gated server-side by the same view-as resolver.
  'pro_my_drawings', 'pro_drawing_entrants',
  // ACADEMY-1: the upcoming classes at the studio — a read; the Going tap (academy_set_going) stays refused under view-as.
  'academy_upcoming',
]);
function readonlyStub(): unknown {
  const p = Promise.resolve({ data: null, error: { message: RO_MSG } });
  return new Proxy(p, {
    get(t, k) {
      if (k === 'then' || k === 'catch' || k === 'finally') {
        return (t as never as Record<string, (...a: unknown[]) => unknown>)[k as string].bind(t);
      }
      return () => readonlyStub();
    },
  });
}
if (!isCell()) {
  const rawFrom = supabase.from.bind(supabase);
  (supabase as { from: typeof supabase.from }).from = ((table: string) => {
    const b = rawFrom(table);
    if (viewAsProId()) {
      for (const m of ['insert', 'update', 'upsert', 'delete'] as const) {
        (b as unknown as Record<string, unknown>)[m] = () => readonlyStub();
      }
    }
    return b;
  }) as typeof supabase.from;

  const rawRpc = supabase.rpc.bind(supabase);
  (supabase as { rpc: typeof supabase.rpc }).rpc = ((fn: string, ...rest: unknown[]) => {
    if (viewAsProId() && !READ_RPCS.has(fn)) return readonlyStub();
    return (rawRpc as (...a: unknown[]) => unknown)(fn, ...rest);
  }) as typeof supabase.rpc;

  const rawStorageFrom = supabase.storage.from.bind(supabase.storage);
  supabase.storage.from = ((bucket: string) => {
    const st = rawStorageFrom(bucket);
    if (viewAsProId()) {
      for (const m of ['upload', 'update', 'remove', 'move', 'copy'] as const) {
        (st as unknown as Record<string, unknown>)[m] = () => readonlyStub();
      }
    }
    return st;
  }) as typeof supabase.storage.from;

  const rawInvoke = supabase.functions.invoke.bind(supabase.functions);
  supabase.functions.invoke = ((...a: unknown[]) => {
    if (viewAsProId()) return readonlyStub();
    return (rawInvoke as (...x: unknown[]) => unknown)(...a);
  }) as typeof supabase.functions.invoke;
}
